// src/components/Wizard/LessonWizard.jsx
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../Layout/Layout'
import StimulusStep from './StimulusStep'
import QuestionWizard from './QuestionWizard'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiFileText, FiArrowLeft } = FiIcons

// We now only have 2 steps: stimulus -> questions.
// The actual plan generation happens on the lesson detail page.
const STEPS = ['stimulus', 'questions']

const SUBJECT_OPTIONS = [
  'English',
  'Mathematics',
  'Science',
  'History',
  'Geography',
  'Other',
]

const LessonWizard = () => {
  const navigate = useNavigate()

  const [currentStep, setCurrentStep] = useState('stimulus')
  const [lessonDesign, setLessonDesign] = useState(null)
  const [subject, setSubject] = useState(null)

  const handleSubjectChange = (newSubject) => {
    setSubject(newSubject)
    // keep any existing lessonDesign in sync, if it already exists
    setLessonDesign((prev) =>
      prev ? { ...prev, subject: newSubject } : prev,
    )
  }

  // 1) After stimulus step completes
  const handleStimulusComplete = ({ lessonDesign: createdDesign, stimulus, themes }) => {
    console.log('Stimulus step complete:', { createdDesign, stimulus, themes })
    setLessonDesign(createdDesign)
    if (createdDesign?.subject && !subject) {
      setSubject(createdDesign.subject)
    }
    setCurrentStep('questions')
  }

  // 2) After questions step completes, go straight to the lesson detail page
  const handleQuestionsComplete = (updatedDesign) => {
    console.log('Question wizard complete, updated lessonDesign:', updatedDesign)
    setLessonDesign(updatedDesign)

    // Navigate to the main lesson view where the real plan generation UI lives
    if (updatedDesign?.id) {
      navigate(`/lesson/${updatedDesign.id}`)
    } else if (lessonDesign?.id) {
      // Fallback in case updatedDesign is missing id for some reason
      navigate(`/lesson/${lessonDesign.id}`)
    } else {
      console.error('No lesson design id available after questions step')
    }
  }

  const renderStep = () => {
    if (currentStep === 'stimulus') {
      return (
        <StimulusStep
          subject={subject}                 // ✅ new
          onSubjectChange={handleSubjectChange} // (optional) if you want badges inside StimulusStep too
          onComplete={handleStimulusComplete}
        />
      )
    }

    if (currentStep === 'questions') {
      if (!lessonDesign) {
        // Safety guard: if somehow we got here without a lessonDesign, send back
        return (
          <div className="space-y-4">
            <p className="text-red-600">
              Something went wrong – no lesson design found. Please start again.
            </p>
            <button
              onClick={() => setCurrentStep('stimulus')}
              className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              <span>Back to start</span>
            </button>
          </div>
        )
      }

      return (
        <QuestionWizard
          lessonDesign={lessonDesign}
          onComplete={handleQuestionsComplete}
        />
      )
    }

    return null
  }

  const stepIndex = Math.max(0, STEPS.indexOf(currentStep))
  const totalSteps = STEPS.length

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-8 space-y-6">
        {/* Step indicator */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Step {stepIndex + 1} of {totalSteps}
            </span>
            <span className="font-medium capitalize">{currentStep}</span>
          </div>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Intro header (changes subtly by step) */}
        {currentStep === 'stimulus' && (
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiFileText} className="w-6 h-6 text-blue-600" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  New lesson design
                </h2>
                <p className="text-gray-600">
                  First, choose the subject, then add your stimulus and key themes.
                </p>
              </div>
            </div>

            {/* ✅ subject badges */}
            <div className="flex flex-wrap gap-2">
              {SUBJECT_OPTIONS.map((option) => {
                const isSelected = subject === option
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleSubjectChange(option)}
                    className={[
                      'px-3 py-1 rounded-full text-sm border transition-colors',
                      isSelected
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                    ].join(' ')}
                  >
                    {option}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {currentStep === 'questions' && (
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiFileText} className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Lesson details
              </h2>
              <p className="text-gray-600">
                Answer a few quick questions so we can shape the lesson plan.
                When you&apos;re done, you&apos;ll go straight to the lesson
                page to generate your plan.
              </p>
            </div>
          </div>
        )}

        {renderStep()}
      </div>
    </Layout>
  )
}

export default LessonWizard